import React from 'react';
import { Check } from 'lucide-react';
import './StepIndicator.css';

interface StepConfig {
    label?: string;
    id?: number;
}

interface StepIndicatorProps {
    steps: StepConfig[];
    currentStep: number;
    onStepClick?: (stepNumber: number) => void;
    showProgressBar?: boolean;
    showLabels?: boolean;
    completedIcon?: 'checkmark' | 'number';
    allowClickAhead?: boolean;
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
    steps,
    currentStep,
    onStepClick,
    showProgressBar = false,
    showLabels = false,
    completedIcon = 'number',
    allowClickAhead = false
}) => {
    const totalSteps = steps.length;
    const progress = (currentStep / totalSteps) * 100;

    const isClickable = (stepNumber: number) => {
        if (stepNumber === currentStep) return false;
        if (stepNumber < currentStep) return true;
        if (allowClickAhead && stepNumber > currentStep) return true;
        return false;
    };

    const handleClick = (stepNumber: number) => {
        if (isClickable(stepNumber) && onStepClick) {
            onStepClick(stepNumber);
        }
    };

    return (
        <div className="step-indicator-wrapper">
            <div className="step-indicator">
                {steps.map((step, index) => {
                    const stepNumber = index + 1;
                    const isActive = stepNumber === currentStep;
                    const isCompleted = stepNumber < currentStep;
                    const clickable = isClickable(stepNumber);

                    return (
                        <React.Fragment key={stepNumber}>
                            <button
                                type="button"
                                className={`step-indicator-item ${
                                    isActive ? 'active' : ''
                                } ${isCompleted ? 'completed' : ''} ${
                                    clickable ? 'clickable' : ''
                                }`}
                                onClick={() => handleClick(stepNumber)}
                                disabled={!clickable}
                            >
                                <div className="step-indicator-dot">
                                    {isCompleted && completedIcon === 'checkmark' ? (
                                        <Check size={18} />
                                    ) : (
                                        stepNumber
                                    )}
                                </div>
                                {showLabels && step.label && (
                                    <div className="step-indicator-label">
                                        {step.label}
                                    </div>
                                )}
                            </button>
                            {index < totalSteps - 1 && (
                                <div
                                    className={`step-indicator-line ${
                                        stepNumber < currentStep ? 'completed' : ''
                                    }`}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            {showProgressBar && (
                <div className="step-indicator-progress">
                    <div
                        className="step-indicator-progress-bar"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            )}
        </div>
    );
};

export default StepIndicator;
